import { NumberField, TextField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { useI18n, Trans, useLocale } from '~/common/context';
import { Flex, NumberInput, Text } from '@wyz/ui';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuoteServicePricingPolicyForm } from '~/purchase/components';
import {
  getQuoteServicePriceTypeTranslation,
  QuoteServicePricingPolicyHelpers,
} from '~/purchase/helpers';
import {
  IPriceType,
  IQuoteServiceGeometryStepForm,
  ITyreCountType,
} from '~/purchase/types';
import { useState } from 'react';
import { DEFAULT_EURO_SIGN } from '@wyz/utils';

type GeometryTableProps = {
  selectedVehicleTypeGroup: string;
  priceType: IPriceType;
};

const GeometryTable: React.FC<GeometryTableProps> = ({
  selectedVehicleTypeGroup,
  priceType,
}) => {
  const { t } = useI18n();
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const methods = useFormContext<IQuoteServiceGeometryStepForm>();
  const watchedRefineByTyreType = useWatch({
    control: methods.control,
    name: 'refinementFlags.tyreType',
  });
  const tyreTypeColumns = QuoteServicePricingPolicyHelpers.getTyreTypeColumns(
    t,
    pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
    selectedVehicleTypeGroup,
    watchedRefineByTyreType,
  );
  const [defaultValue, setDefaultValue] = useState<
    Record<ITyreCountType, number | ''>
  >({
    ALL: '',
    ONE_TYRE: '',
    TWO_TYRE: '',
    FOUR_TYRE: '',
  });
  const { state } = usePurchaseContext();
  const locale = useLocale();
  const currencySymbol = state.targetCustomer?.deliveryInformation?.country
    .currencyIsoCode
    ? locale.formatters.currency(
        state.targetCustomer?.deliveryInformation?.country.currencyIsoCode,
      )
    : DEFAULT_EURO_SIGN;

  return (
    <div>
      <table className='service-refinement-table'>
        <thead>
          <tr>
            <th style={{ width: '10%' }}>
              <Trans i18nKey='app.common.product.tyres' />
            </th>
            <th style={{ width: '10%' }}>
              <Trans i18nKey='app.common.service.quote.code' />
            </th>
            <th style={{ width: '20%' }}>
              <Trans i18nKey='app.common.description' />
            </th>
            {watchedRefineByTyreType && (
              <th style={{ width: '10%' }}>
                <Trans
                  i18nKey={getQuoteServicePriceTypeTranslation(
                    priceType,
                    'unit',
                  )}
                />
              </th>
            )}
            {tyreTypeColumns.map((tyreType) => (
              <th key={tyreType.value}>
                {watchedRefineByTyreType
                  ? tyreType.label
                  : t(getQuoteServicePriceTypeTranslation(priceType, 'unit'))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {QuoteServicePricingPolicyHelpers.getTyreCountColumns(
            pricingPolicyForm.tyreCounts.optionItems,
            true,
            ['ONE_TYRE'],
          ).map((tyreCountItem) => (
            <tr key={tyreCountItem.value}>
              <td>{tyreCountItem.label}</td>
              <td align='center'>
                <TextField
                  name={`geometry.tyreCounts.${tyreCountItem.value}.code`}
                  placeholder={t('app.common.service.quote.code')}
                  formGroupClassName=''
                  feedbackType='tooltip'
                />
              </td>
              <td align='center'>
                <TextField
                  name={`geometry.tyreCounts.${tyreCountItem.value}.description`}
                  placeholder={t('app.common.description')}
                  formGroupClassName=''
                  feedbackType='tooltip'
                />
              </td>
              {watchedRefineByTyreType && (
                <td>
                  {pricingPolicyForm.mode === 'WRITE' && (
                    <NumberInput
                      name={`defaultValue.${tyreCountItem.value}`}
                      withKnobs={false}
                      size='sm'
                      placeholder='xx'
                      value={defaultValue[tyreCountItem.value] as number}
                      onChange={({ target }) => {
                        const value = Number(target.value);
                        setDefaultValue({
                          ...defaultValue,
                          [tyreCountItem.value]: value,
                        });
                        tyreTypeColumns.map((vehicleTypeItem) => {
                          methods.setValue(
                            `geometry.tyreCounts.${tyreCountItem.value}.tyreTypes.${vehicleTypeItem.value}.value`,
                            value,
                            {
                              shouldValidate: true,
                            },
                          );
                        });
                      }}
                    />
                  )}
                </td>
              )}
              {tyreTypeColumns.map((vehicleTypeItem) => (
                <td key={`${tyreCountItem.value}.${vehicleTypeItem.value}`}>
                  <Flex alignItems='center'>
                    <div className='max-w-50'>
                      <NumberField
                        name={`geometry.tyreCounts.${tyreCountItem.value}.tyreTypes.${vehicleTypeItem.value}.value`}
                        withKnobs={false}
                        size='sm'
                        placeholder='xx'
                        min={0}
                        feedbackType='tooltip'
                        onChangeValue={() =>
                          setDefaultValue({
                            ...defaultValue,
                            [tyreCountItem.value]: '',
                          })
                        }
                        sign={currencySymbol}
                      />
                    </div>
                    <Text
                      color='secondary-gray'
                      weight='bold'
                      heading='xs'
                      className='p-2'
                    >
                      <Trans
                        i18nKey={getQuoteServicePriceTypeTranslation(priceType)}
                      />
                    </Text>
                  </Flex>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GeometryTable;
